import { Box } from '@chakra-ui/react';
import { AddInstantBillFormWidget, AddInstantBillFormWidgetFields } from '@melio/ap-widgets';
import { Group, NakedButton, Text, useFormSubmissionController } from '@melio/penny';
import { useAnalyticsView } from '@melio/platform-analytics';
import { AccountingPlatform } from '@melio/platform-api';
import { FormattedMessage, useMelioIntl } from '@melio/platform-i18n';
import { forwardRef } from '@melio/platform-utils';

import { NewSinglePaymentStepLayout } from '../../../../NewSinglePaymentStepLayout';

type AddInstantBillScreenProps = {
  isSaving?: boolean;
  isLoading?: boolean;
  defaultValues?: Partial<AddInstantBillFormWidgetFields>;
  onClose: VoidFunction;
  onGoToDashboard: VoidFunction;
  activeAccountingPlatform?: AccountingPlatform;
  inputVendorAsFreeText?: boolean;
  onDone: (data: AddInstantBillFormWidgetFields) => void;
};

export const AddInstantBillScreen = forwardRef<AddInstantBillScreenProps, 'div'>(
  (
    {
      isSaving,
      defaultValues,
      onClose,
      onDone,
      isLoading,
      onGoToDashboard,
      inputVendorAsFreeText,
      activeAccountingPlatform,
      ...props
    },
    ref
  ) => {
    const { formatMessage } = useMelioIntl();
    const { onSubmissionStateChange, submitButtonProps } =
      useFormSubmissionController<AddInstantBillFormWidgetFields>();

    useAnalyticsView('AddBillInfo');

    return (
      <NewSinglePaymentStepLayout
        data-component="AddInstantBillActivity.AddInstantBillScreen"
        data-testid="add-instant-bill-activity-add-instant-bill-screen"
        isLoading={isLoading}
        {...props}
        ref={ref}
        headerContent={
          <NewSinglePaymentStepLayout.Header>
            <NewSinglePaymentStepLayout.CloseButton onClick={onClose} />
          </NewSinglePaymentStepLayout.Header>
        }
        footerContent={
          <NewSinglePaymentStepLayout.Actions>
            <Box width="100%">
              <Group variant="vertical" textAlign="center">
                <NewSinglePaymentStepLayout.NextButton
                  {...submitButtonProps}
                  label={formatMessage('activities.addInstantBill.screens.addInstantBill.continue')}
                />
                <Text textStyle="body4" color="neutral.darkest">
                  <FormattedMessage
                    id="activities.addInstantBill.screens.addInstantBill.uploadFromQuickbooks"
                    values={{
                      link: (
                        <NakedButton
                          variant="secondary"
                          size="small"
                          onClick={onGoToDashboard}
                          data-testid="upload-from-quickbooks-button"
                          label={formatMessage(
                            'activities.addInstantBill.screens.addInstantBill.uploadFromQuickbooksLink'
                          )}
                        />
                      ),
                    }}
                  />
                </Text>
              </Group>
            </Box>
          </NewSinglePaymentStepLayout.Actions>
        }
      >
        <NewSinglePaymentStepLayout.Title>
          {formatMessage('activities.addInstantBill.screens.addInstantBill.title')}
        </NewSinglePaymentStepLayout.Title>
        <NewSinglePaymentStepLayout.Description>
          {formatMessage('activities.addInstantBill.screens.addInstantBill.description')}
        </NewSinglePaymentStepLayout.Description>
        <NewSinglePaymentStepLayout.Content>
          <AddInstantBillFormWidget
            onSubmit={onDone}
            isSaving={isSaving}
            defaultValues={defaultValues}
            onSubmissionStateChange={onSubmissionStateChange}
            activeAccountingPlatform={activeAccountingPlatform}
            inputVendorAsFreeText={inputVendorAsFreeText}
          />
        </NewSinglePaymentStepLayout.Content>
      </NewSinglePaymentStepLayout>
    );
  }
);

AddInstantBillScreen.displayName = 'AddInstantBillActivity.AddInstantBillScreen';
